import { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation, useParams } from 'react-router-dom'
import { useMediaQuery } from 'react-responsive'
import { useIntl } from 'react-intl'
import { throttle } from 'lodash';
import toastr from 'toastr';

import { useLang } from '../../../_metronic/i18n/Metronici18n';

import { setCurrentChat, useChat } from '../../context/chat';
import { patchChat, addChat, unshiftCurrentChat } from '../../context/chat'

import { ChatMessageModel, ChatMessageRoleType, ChatMessageGenerationType, ChatUploadedFile, SharedChatModel, TEXT_GENERATION_TYPES } from '../../models';
import { chatMessageAPI } from '../../api'
import { downloadObjects, setTitle, determineDirection } from '../../utils';

import { ChatFooter } from './components/ChatFooter';
import { ChatMessage } from './components/ChatMessage'

import ChatShareOptions from './components/ChatShareOptions'

import {ChatMobileHeader} from '../../layouts/ChatContent'

import { useAuth } from '../../context/auth';


interface ChatProps {
  handleStartNewChat: () => void;
  currentSharedChat?: SharedChatModel;
  handleDeleteChat?: (chatId: string) => void;
}

const ChatBase: React.FC<ChatProps> = ({handleStartNewChat, currentSharedChat, handleDeleteChat}) => {
  
  const location = useLocation()

  const isSharedChat = location.pathname.includes('/s/')

  const { sharedChatId } = useParams<{ sharedChatId: string}>();

  const intl = useIntl()

  const selectedLang = useLang()

  const navigate = useNavigate()

  const isMobile = useMediaQuery({ maxWidth: 991.98 });

  const { currentUser } = useAuth()

  const { state : { currentChat }, dispatch } = useChat()

  const [currentChatId, setCurrentChatId] = useState<string | null>(null)

  const [ chatMessages, setChatMessages ] = useState<Array<ChatMessageModel>>([])

  const [ numberOfChatMessagesWithImage, setNumberOfChatMessagesWithImage ] = useState(0)
  const [ loadedChatMessagesWithImage, setLoadedChatMessagesWithImage ] = useState(0)

  const [isLoadingChatMessages, setIsLoadingChatMessages] = useState(true)

  const bottomRef = useRef<HTMLDivElement | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [showToBottomButton, setShowToBottomButton] = useState(false);

  const [isSending, setIsSending] = useState(false)
  const [isTyping, setIsTyping] = useState(false)

  const [currentGenerationType, setCurrentGenerationType] = useState<ChatMessageGenerationType>(ChatMessageGenerationType.TEXT)
  const [currentUploadedFiles, setCurrentUploadedFiles] = useState<ChatUploadedFile[]>([])

  const [allUploadedFiles, setAllUploadedFiles] = useState<ChatUploadedFile[]>([])

  useEffect(() => {
    if( numberOfChatMessagesWithImage === loadedChatMessagesWithImage ){
      scrollBottom();
    }
  }, [loadedChatMessagesWithImage])

  useEffect(() => {
    const handleToBottomButton_ = throttle(() => {
	    if( scrollContainerRef.current ){
	      const scrollContainer = scrollContainerRef.current;

	      if (scrollContainer.scrollHeight - scrollContainer.scrollTop > scrollContainer.clientHeight+100) {
	        setShowToBottomButton(true);
	      } else {
	        setShowToBottomButton(false);
	      }
	    }
    }, 200)

    if( scrollContainerRef.current ){
    	scrollContainerRef.current.addEventListener('scroll', handleToBottomButton_);
    }

    return () => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.removeEventListener('scroll', handleToBottomButton_);
      }
    };
  }, [])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsTyping(false)
        setTimeout(scrollBottom, 1)
      }
    };

    if (isTyping) {
      document.addEventListener('keydown', handleKeyDown)
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    };
  }, [isTyping]);

  useEffect(() => {
    if( currentChat ){
      if( currentChat.id != currentChatId && (currentChat.is_new == null || !currentChat.is_new )  ){
        setIsLoadingChatMessages(true)
        setShowToBottomButton(false)
        setIsSending(false)
        setIsTyping(false)
        fetchChatMessages(currentChat.id)
        setCurrentGenerationType(ChatMessageGenerationType.TEXT)
        setCurrentUploadedFiles([])
      }else if( currentChat.is_new ){
        dispatch(patchChat({...currentChat, 'is_new': false}))
      }
    }else{
      setIsSending(false)
      setIsTyping(false)
      setIsLoadingChatMessages(false)
      setShowToBottomButton(false)
      setChatMessages([])
      setCurrentGenerationType(ChatMessageGenerationType.TEXT)
      setCurrentUploadedFiles([])
    }
    setCurrentChatId(currentChat ? currentChat.id : null)
    setTitle(currentChat ? currentChat.name : '')
  }, [currentChat])

  useEffect(() => {
    if( sharedChatId ){
      fetchChatMessages(sharedChatId)
    }
  }, [sharedChatId])

  useEffect(() => {
    const timer = setTimeout(scrollBottom, 1)
    return () => clearTimeout(timer);
  }, [chatMessages, isLoadingChatMessages])

  const createNewController = () => {
    return new AbortController()
  }

  const scrollBottom = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
    }
  }

  const scrollToBottom = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({ 
        top: scrollContainerRef.current.scrollHeight, 
        behavior: "smooth" 
      });
    }
  }

  const keepScrollToBottom = () => {
    if (scrollContainerRef.current ) {
      const container = scrollContainerRef.current;
      const scrollPosition = container.scrollHeight - container.scrollTop;
      const containerHeight = container.clientHeight;
      if (scrollPosition <= containerHeight + 50) {
        scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
      }
    }
  }

  const fetchChatMessages = async (chatId: string) => {
    if( chatId ){
      try{
        const {data : data} = await chatMessageAPI.getAll(chatId, isSharedChat)
        setChatMessages(data['data'])
        loadUploadedFiles(data['data'])
        setNumberOfChatMessagesWithImage(data['data'].filter(message => message.has_image).length)
        setLoadedChatMessagesWithImage(0)
      }catch(e : any){
        if (e.response) {
          const { status } = e.response;
          if (status === 404) {
            if( isSharedChat ){
              toastr.info(intl.formatMessage({id: 'chat.share.not_found'}))
            }
            dispatch(setCurrentChat())
            navigate(`/c`)
          }
        }
      }
    }
    setIsLoadingChatMessages(false)
  }

  const downloadChatMessages = async () => {
    if( currentChat ){
      const {data} = await chatMessageAPI.getAll(currentChat.id);
      const chatMessages = data['data'].map(message => {
        if (message.created_dt) {
          const localTime = new Date(message.created_dt).toLocaleString(); // Converts to local time
          return { ...message, created_dt: localTime, role: intl.formatMessage({id: 'chat.message.role.' + message.role})};
        }
        return message;
      });
      const headers = ["role", "message", "reaction",	"created_dt"]
      downloadObjects('excel', chatMessages.filter(message => message.id != undefined), 'messages', headers)
    }
  }

  const moveCurrentChatToTop = () => {
    if( currentChat ){
      dispatch(patchChat({...currentChat, 'recent_message_created_dt': new Date()}))
      dispatch(unshiftCurrentChat())
    }
  }

  const sendMessage = async (message: string, generationType: ChatMessageGenerationType, needDeepThinking: boolean) => {

    if( ChatMessageGenerationType.TRANSCRIPTION === generationType || !TEXT_GENERATION_TYPES.includes(generationType) ){
      setCurrentUploadedFiles([])
      setCurrentGenerationType(ChatMessageGenerationType.TEXT)
    }

    const controler = createNewController()

    setIsSending(true)

    const pairedOnForMessage = new Date().getTime();
    const newMessage: ChatMessageModel = {
      chat_id: currentChat ? currentChat.id : 'new',
      role: ChatMessageRoleType.USER,
      message: message,
      generation_type: generationType,
      paired_on: pairedOnForMessage,
      chat_uploaded_files: currentUploadedFiles,
      need_deep_thinking: needDeepThinking
    };

    const pairedOnForMessageResponse = new Date().getTime()+1;
    const newMessageResponse: ChatMessageModel = {
      chat_id: currentChat ? currentChat.id : 'new',
      role: ChatMessageRoleType.ASSISTANT,
      message: "",
      generation_type: generationType,
      paired_on: pairedOnForMessageResponse
    };

    setChatMessages(prevChatMessages =>(currentChat ?  [
      ...prevChatMessages,
      newMessage,
      newMessageResponse
    ]:[newMessage,newMessageResponse]));

    let chatMessage: ChatMessageModel | undefined = undefined
    let chatResponseMessage: ChatMessageModel | undefined = undefined
    let chatName: string | undefined = undefined

    try {
      const response = await chatMessageAPI.sendAndGetResponse( 
        currentChat ? currentChat.id : 'new', 
        selectedLang, 
        { 
          'message': message, 
          'generation_type': generationType,
          'uploaded_file_id': currentUploadedFiles.filter(f => f.id).map(f => f.id),
          'uploaded_file': currentUploadedFiles.filter(f => !f.id).map(f => f.file),
          'need_deep_thinking': needDeepThinking,
        },
        controler.signal
      );
      chatMessage = response.data.data.message
      chatResponseMessage = response.data.data.response_message
      chatName = response.data.data.chat_name
    }catch(e){
      removeFailedMessageAndResponse(pairedOnForMessage, pairedOnForMessageResponse)
      console.error(e)
    }

    if (chatMessage){
      if ( chatMessage.chat_uploaded_file_ids && currentUploadedFiles && chatMessage.generation_type !== ChatMessageGenerationType.TRANSCRIPTION ) {
        const updatedUploadedFiles = currentUploadedFiles.map((file, index) => {
          if (file.id === undefined && chatMessage && chatMessage.chat_uploaded_file_ids) {
            return { ...file, id: chatMessage.chat_uploaded_file_ids[index], file: undefined };
          }
          return file;
        });
        setCurrentUploadedFiles(updatedUploadedFiles);
      }
      
      setChatMessages(prevMessages => {
        return prevMessages.map(msg =>
          (msg.paired_on === pairedOnForMessage && msg.role === ChatMessageRoleType.USER)
            ? { ...msg, ...chatMessage }
            : (msg.paired_on === pairedOnForMessageResponse && msg.role === ChatMessageRoleType.ASSISTANT)
              ? 
              (
                chatResponseMessage
                ?
                  { ...msg, ...chatResponseMessage } 
                : 
                  chatMessage && chatMessage.is_filtered
                  ?
                  null
                  :
                  { ...msg, 'is_failed' : true } 
              )
              : msg
        ).filter((msg): msg is ChatMessageModel => msg !== null);
      });

      if( chatMessage.has_exceeded_safety_filter_limit ){
        toastr.error(selectedLang == 'en' ? "You've exceeded the safety limit, and your account is blocked for 24 hours. You’ll be logged out in 5 seconds" : "لقد تجاوزت حد الأمان، وتم حظر حسابك لمدة 24 ساعة. سيتم تسجيل الخروج خلال 5 ثواني");
        setTimeout(()=> {
          navigate('/a/sign-out')
        }, 5000);
      }

      moveCurrentChatToTop()

      if( currentChat == null && chatMessage ){
        const chat = {
          'id': chatMessage.chat_id,
          'name': chatName ? chatName : message,
          'created_dt': new Date(),
          'is_new': true
        }
        dispatch(addChat(chat))
        dispatch(setCurrentChat(chatMessage.chat_id))
        navigate(`/c/${chatMessage.chat_id}`)
      }
      setIsTyping(chatResponseMessage != null && (chatResponseMessage.is_filtered == null || !chatResponseMessage.is_filtered ) && TEXT_GENERATION_TYPES.includes(chatResponseMessage.generation_type))
    }
      
    setIsSending(false)
  }

  const removeFailedMessageAndResponse = (pairedOnForMessage?:number, pairedOnForResponse?:number) => {
    setChatMessages(prevMessages => {
      return prevMessages.filter(msg =>
        (msg.paired_on !== pairedOnForMessage && msg.paired_on !== pairedOnForResponse)
      );
    });
  }

  const setFailedToGetMessageResponse = (pairedOn:number) => {
    setChatMessages(prevMessages => {
      return prevMessages.map(msg =>
        (msg.paired_on === pairedOn && msg.role === ChatMessageRoleType.ASSISTANT)
        ? { ...msg, 'is_failed' : true }
        : msg
      );
    });
  }

  const regenerate = async (responseTo:number, generationType:ChatMessageGenerationType) => {
    if( chatMessages && currentChat ){
      const controler = createNewController()
      setIsSending(true)
      const pairedOnForMessageResponse = new Date().getTime();

      setChatMessages(prevMessages => {
        const messages = prevMessages.filter(msg => msg.response_to !== responseTo);
        return [...messages, { chat_id: currentChat.id, role: ChatMessageRoleType.ASSISTANT, message: "", generation_type: generationType, paired_on : pairedOnForMessageResponse}];
      });

      try {
        const {data} = await chatMessageAPI.getResponse(currentChat.id, responseTo, controler.signal)
        setChatMessages(prevMessages => {
          return prevMessages.map(msg =>
            (msg.paired_on === pairedOnForMessageResponse)
            ? (data['data']['response_message'] ? { ...msg, ...data['data']['response_message'], is_regenerated : true } : { ...msg, 'is_failed' : true } )
            : msg
          );
        });
        moveCurrentChatToTop()
        setIsTyping(data['data']['response_message'] != null && TEXT_GENERATION_TYPES.includes(generationType) )
      }catch(e){
        setFailedToGetMessageResponse(pairedOnForMessageResponse)
        console.error(e)
      }
      setIsSending(false)
    }
  }
  
  const loadUploadedFiles = async (chatMessages: ChatMessageModel[]) => {
    if( (sharedChatId || currentChat) && chatMessages.length > 0 ){
      const uniqueFileIds = Array.from(
        new Set(
          chatMessages.flatMap(
            (chatMessage) => chatMessage.chat_uploaded_file_ids || []
          )
        )
      )
      uniqueFileIds.forEach( (fileId: string) => {
        chatMessageAPI.getUploadedFile(sharedChatId ? sharedChatId : currentChat?.id || '', fileId, isSharedChat, true).then( (response) => {
          if( response.status === 200 && response.data.data ){
            const uploadedFile: ChatUploadedFile = {
              id: response.data.data.id,
              base64Url: response.data.data.encoded_file,
              type: response.data.data.type,
              thumbnailBase64Url: response.data.data.encoded_thumbnail
            };
            setAllUploadedFiles((prev) => [...prev, uploadedFile]);
          }
        })
      })
    }
  }
    
  return (
    <>
      {currentChat && chatMessages && currentUser?.email && (
        <div className={`position-absolute d-none d-xl-flex top-0 me-18 mt-4 end-0 z-index-1`}>
          <ChatShareOptions
            chatId={currentChat.id}
            deleteChat={()=>handleDeleteChat && handleDeleteChat(currentChat.id)}
            downloadChatMessages={downloadChatMessages}
            lastestChatMessage={chatMessages[chatMessages.length-1]}
          />
        </div>
      )}
      <div className="card h-100 w-100 rounded-0 chat-area bg-transparent border-0 flex-center">
        <ChatMobileHeader currentUser={currentUser} handleStartNewChat={handleStartNewChat} currentSharedChat={currentSharedChat}/>
        <div className={`${!isMobile && !isSharedChat && (currentChat?.id === undefined || chatMessages.length === 0 ) && !isSending && !isLoadingChatMessages ? '' : 'card-body h-100'} w-100 p-3 mt-2 mt-lg-15 px-md-0 overflow-y-auto`} ref={scrollContainerRef}>
          { chatMessages.length === 0 && !isSending && !isLoadingChatMessages
          ?
            <div className={`h-100 d-flex flex-center ${!isMobile && 'pb-10'}`}>
              <img src="/images/logos/full-logo.svg" className="mw-150px mw-md-200px theme-light-show" alt=""/>
              <img src="/images/logos/white-full-logo.svg" className="mw-150px mw-md-200px theme-dark-show" alt=""/>
            </div>
          :
            <div className="w-xl-800px h-100 mx-auto chat-messages">
              {!isMobile && currentSharedChat && currentSharedChat.name && (
                <>
                  <div style={{direction: determineDirection(currentSharedChat.name)}}>
                    <div className="text-dark fw-bold h1 display-6">{currentSharedChat.name}</div>
                  </div>
                  <div className="separator mb-10 border-gray-300"></div>
                </>
              )}
              {chatMessages.length > 0 && chatMessages.map((message, index) => (
                <ChatMessage 
                  key={message.id} 
                  allUploadedFiles={allUploadedFiles}
                  updateUploadedFileBase64Url={(fileId: string, base64Url: string) => {
                    setAllUploadedFiles((prev) => {
                      const updatedFiles = prev.map((file) => {
                        if (file.id === fileId) {
                          return { ...file, base64Url };
                        }
                        return file;
                      });
                      return updatedFiles;
                    });
                  }}
                  chatMessage={message} 
                  withTyping={chatMessages.length-1 == index && isTyping} 
                  isTyping={isTyping} 
                  setIsTyping={setIsTyping} 
                  isSending={isSending}
                  handleScroll={keepScrollToBottom}
                  scrollBottom={scrollBottom}
                  handleRegenerate={regenerate}
                  isLast={chatMessages.length-1 == index}
                  responseToChatMessage={index > 0 && chatMessages[index-1].id === message.response_to ? chatMessages[index-1] : undefined}
                  handleOnLoadImage={() => {
                    setLoadedChatMessagesWithImage((prev) => prev + 1)
                  }}
                />
              ))}
              <div ref={bottomRef}></div>
            </div>
          }
        </div>
        <ChatFooter 
          scrollToBottom={scrollToBottom}
          showToBottomButton={showToBottomButton}
          currentGenerationType={currentGenerationType}
          setCurrentGenerationType={setCurrentGenerationType}
          currentUploadedFiles={currentUploadedFiles}
          setCurrentUploadedFiles={setCurrentUploadedFiles}
          isLoadingChatMessages={isLoadingChatMessages} 
          isSending={isSending} 
          isTyping={isTyping} 
          stopTyping={() => {
            setIsTyping(false)
            setTimeout(() => scrollToBottom(), 1)
          }} 
          sendMessage={sendMessage}
        />
      </div>
    </>
  )
}

export {ChatBase}
